import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import {
  LayoutDashboard, Globe, Users as UsersIcon, Bot, Search, BarChart3, Zap,
  MessageSquare, Image as ImageIcon, Star, FileText, ShieldCheck, CreditCard,
  Settings, Building2, LogOut, ChevronDown, Bell, Search as SearchIcon,
  Sparkles, Palette,
} from "lucide-react";
import { useState } from "react";
import { BRAND } from "@/config/brand";

const PLUGITAI_LOGO = BRAND.logos.plugitaiDark;

// Sidebar grouped per PlugitAI structure (Business · AI & Automation · Platform).
// Each item's `group` maps to the labeled section header rendered below.
const NAV = [
  { to: "/admin",                 label: "Dashboard",     icon: LayoutDashboard, end: true, color: "blue", group: "business" },
  { to: "/admin/website/pages",   label: "Websites",      icon: Globe,           color: "blue",  group: "business" },
  { to: "/admin/leads",           label: "CRM",           icon: UsersIcon,       color: "blue",  group: "business" },
  { to: "/admin/seo",             label: "SEO",           icon: Search,          color: "green", group: "business" },
  { to: "/admin/analytics",       label: "Analytics",     icon: BarChart3,       color: "blue",  group: "business" },
  { to: "/admin/media",           label: "Media Library", icon: ImageIcon,       color: "green", group: "business" },
  { to: "/admin/forms",           label: "Forms",         icon: FileText,        color: "blue",  group: "business" },
  { to: "/admin/reviews",         label: "Reviews",       icon: Star,            color: "orange", group: "business" },
  { to: "/admin/billing",         label: "Billing",       icon: CreditCard,      color: "green", group: "business" },

  { to: "/admin/ai",              label: "BotProviderAI", icon: Bot,             color: "orange", group: "ai" },
  { to: "/admin/messages",        label: "Messages",      icon: MessageSquare,   color: "blue",   group: "ai" },
  { to: "/admin/conversations",   label: "Conversation Intel", icon: MessageSquare, color: "pink", group: "ai", accent: "pink", badge: "New" },
  { to: "/admin/automation",      label: "Automation",    icon: Zap,             color: "orange", group: "ai" },

  { to: "/admin/users",           label: "Users",         icon: ShieldCheck,     color: "green", group: "platform" },
  { to: "/admin/brand",           label: "Brand",         icon: Palette,         color: "blue",  group: "platform" },
  { to: "/admin/website",         label: "Settings",      icon: Settings,        color: "blue",  group: "platform" },
];

const NAV_GROUPS = [
  { id: "business", label: "Business" },
  { id: "ai",       label: "AI & Automation" },
  { id: "platform", label: "Platform" },
];

const PLATFORM_NAV = [
  { to: "/admin/platform", label: "Master Dashboard", icon: LayoutDashboard },
  { to: "/admin/tenants",  label: "Tenants",          icon: Building2 },
  { to: "/admin/settings", label: "Integrations",     icon: Sparkles },
];

const ROLE_LABEL = {
  platform_owner: "Platform Owner", platform_admin: "Platform Admin",
  business_owner: "Owner", manager: "Manager", employee: "Member", read_only: "Read only",
  super_admin: "Platform Owner", tenant_admin: "Owner", tenant_user: "Member",
};
const PLATFORM_ROLES = new Set(["platform_owner", "super_admin", "platform_admin"]);

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const { tenants, activeTenant, switchTenant } = useTenant();
  const [tenantOpen, setTenantOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const nav = useNavigate();
  const isSuper = PLATFORM_ROLES.has(user?.role);

  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-[var(--n-bg)] text-[var(--n-text)]">
      {/* Sidebar */}
      <aside className="w-64 bg-[var(--n-paper)] border-r border-[var(--n-border)] flex flex-col" data-testid="admin-sidebar">
        <div className="p-5 border-b border-[var(--n-border)]">
          <Link to="/admin" className="flex items-center gap-3" data-testid="brand-logo">
            {isSuper ? (
              <>
                <img src={PLUGITAI_LOGO} alt="PlugitAI" className="h-11 w-auto" />
                <div className="text-[9px] uppercase tracking-[0.22em] pl-2 border-l border-[var(--n-border)] gold-shine">
                  Powered by<br/>{BRAND.engineName}
                </div>
              </>
            ) : activeTenant ? (
              <>
                {activeTenant.logo_url ? (
                  <img src={activeTenant.logo_url} alt={activeTenant.name} className="h-8"/>
                ) : (
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-cab font-black" style={{ background: activeTenant.primary_color }}>
                    {activeTenant.name[0]}
                  </div>
                )}
                <div className="leading-tight">
                  <div className="font-cab font-black text-base tracking-tight">{activeTenant.name}</div>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--n-text-muted)]">
                    on {BRAND.platformName}
                  </div>
                </div>
              </>
            ) : null}
          </Link>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV_GROUPS.map((group) => {
            const items = NAV.filter((n) => n.group === group.id);
            if (items.length === 0) return null;
            return (
              <div key={group.id} data-testid={`nav-group-${group.id}`}>
                <div className="px-5 mt-4 mb-2 text-[10px] uppercase tracking-[0.22em] text-[var(--n-text-muted)] font-semibold">
                  {group.label}
                </div>
                {items.map(({ to, label, icon: Icon, end, color, accent, badge }) => (
                  <NavLink
                    key={to} to={to} end={end}
                    data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 mx-2 my-0.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                        accent === "pink"
                          ? (isActive
                              ? "bg-[var(--n-pink-soft)] text-[var(--n-pink)] font-bold"
                              : "text-[var(--n-pink)] font-semibold hover:bg-[var(--n-pink-soft)]")
                          : isActive
                          ? `bg-[var(--n-${color}-soft)] text-[var(--n-${color})] font-semibold`
                          : "text-[var(--n-text-sec)] hover:bg-[var(--n-surface)] hover:text-[var(--n-text)]"
                      }`
                    }
                  >
                    <Icon size={16} strokeWidth={2} />
                    <span className="flex-1">{label}</span>
                    {badge && (
                      <span className="text-[9px] font-black uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full"
                            style={{ background: "var(--n-pink)", color: "white" }}>
                        {badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}

          {isSuper && (
            <>
              <div className="px-5 mt-6 mb-2 text-[10px] uppercase tracking-[0.22em] text-[var(--n-text-muted)] font-semibold">
                {BRAND.engineName} Core (owner)
              </div>
              {PLATFORM_NAV.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to} to={to}
                  data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 mx-2 my-0.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-[var(--n-orange-soft)] text-[var(--n-orange)] font-semibold"
                        : "text-[var(--n-text-sec)] hover:bg-[var(--n-surface)] hover:text-[var(--n-text)]"
                    }`
                  }
                >
                  <Icon size={16} strokeWidth={2}/>
                  <span>{label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="p-3 border-t border-[var(--n-border)] space-y-2">
          {activeTenant && (
            <a href={`/site/${activeTenant.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-2 py-2 text-xs text-[var(--n-text-sec)] hover:text-[var(--n-text)]" data-testid="public-site-link">
              <Globe size={12}/> View public site ↗
            </a>
          )}
          <div className="px-2 text-[10px] uppercase tracking-[0.22em]" data-testid="sidebar-footer-credit">
            <span>{BRAND.platformName} · </span>
            <span className="gold-shine">Powered by {BRAND.engineName}</span>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top nav */}
        <header className="h-16 bg-[var(--n-paper)] border-b border-[var(--n-border)] flex items-center px-6 gap-4">
          {isSuper ? (
            <div className="relative">
              <button onClick={() => setTenantOpen((v) => !v)} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--n-surface)]" data-testid="tenant-switcher-dropdown">
                <div className="w-2 h-2 rounded-full" style={{ background: activeTenant?.primary_color || "#2563EB" }}/>
                <span className="font-semibold text-sm">{activeTenant?.name || "Select tenant"}</span>
                <ChevronDown size={14} className="text-[var(--n-text-muted)]"/>
              </button>
              {tenantOpen && (
                <div className="absolute top-full mt-2 left-0 min-w-[240px] n-card p-1 z-50">
                  {tenants.map((t) => (
                    <button key={t.id} onClick={() => { switchTenant(t.id); setTenantOpen(false); }} className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-[var(--n-surface)] flex items-center gap-2" data-testid={`tenant-option-${t.slug}`}>
                      <div className="w-2 h-2 rounded-full" style={{ background: t.primary_color }}/>
                      <span className="font-medium">{t.name}</span>
                      <span className="ml-auto text-[10px] font-mono-plex text-[var(--n-text-muted)]">/{t.slug}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2" data-testid="tenant-workspace-label">
              <div className="w-2 h-2 rounded-full" style={{ background: activeTenant?.primary_color }}/>
              <span className="font-semibold text-sm">{activeTenant?.name} <span className="text-[var(--n-text-muted)] font-normal">· workspace</span></span>
            </div>
          )}

          <div className="flex-1 max-w-md relative">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-text-muted)]"/>
            <input placeholder="Search leads, pages, media…" className="n-input pl-9 py-2 bg-[var(--n-surface)] border-transparent" data-testid="global-search" />
          </div>

          <button className="p-2 rounded-lg hover:bg-[var(--n-surface)] relative" data-testid="notifications">
            <Bell size={16} className="text-[var(--n-text-sec)]"/>
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[var(--n-orange)] pulse-dot"/>
          </button>

          <div className="relative">
            <button onClick={() => setUserMenuOpen((v) => !v)} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--n-surface)]" data-testid="user-menu">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold bg-gradient-to-br from-[var(--n-blue)] to-[var(--n-green)]">
                {(user?.name || user?.email || "?")[0].toUpperCase()}
              </div>
              <div className="hidden md:block text-left">
                <div className="text-xs font-semibold leading-tight">{user?.name || user?.email}</div>
                <div className="text-[10px] text-[var(--n-text-muted)]" data-testid="current-user-role">{ROLE_LABEL[user?.role] || user?.role}</div>
              </div>
            </button>
            {userMenuOpen && (
              <div className="absolute top-full mt-2 right-0 min-w-[200px] n-card p-1 z-50">
                <div className="px-3 py-2 border-b border-[var(--n-border)]">
                  <div className="text-xs font-medium truncate" data-testid="current-user-email">{user?.email}</div>
                </div>
                <button onClick={doLogout} className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-[var(--n-surface)] flex items-center gap-2 text-[var(--n-danger)]" data-testid="logout-button">
                  <LogOut size={14}/> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-auto" data-testid="admin-main">{children}</main>
      </div>
    </div>
  );
}
